import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}
export function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: LegalModalProps) {
  return <AnimatePresence data-id="element-126">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]" onClick={onClose} data-id="element-127" />
          <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 40
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl md:max-h-[80vh] bg-white rounded-2xl shadow-2xl z-[101] flex flex-col overflow-hidden" data-id="element-128">
            <div className="flex items-center justify-between p-6 border-b border-warm-gray" data-id="element-129">
              <h2 className="text-xl font-bold text-navy" data-id="element-130">{title}</h2>
              <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-navy hover:bg-warm-white transition-colors" data-id="element-131">
                <XIcon className="h-5 w-5" data-id="element-132" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto text-gray-700 leading-relaxed space-y-4 text-sm" data-id="element-133">
              {children}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function PrivacyContent() {
  return <>
      <p className="text-xs text-gray-400" data-id="element-134">
        Última actualización: febrero 2026
      </p>

      <h3 className="text-base font-bold text-navy pt-2" data-id="element-135">
        1. Información que recopilamos
      </h3>
      <p data-id="element-136">
        Al adquirir nuestro producto digital, recopilamos únicamente la
        información necesaria para procesar tu compra: nombre, dirección de
        correo electrónico y datos de pago. No recopilamos información sensible
        de salud ni datos biométricos.
      </p>

      <h3 className="text-base font-bold text-navy" data-id="element-137">
        2. Uso de la información
      </h3>
      <p data-id="element-138">Tu información se utiliza exclusivamente para:</p>
      <ul className="list-disc pl-5 space-y-1" data-id="element-139">
        <li data-id="element-140">Procesar y entregar tu compra</li>
        <li data-id="element-141">Enviarte el producto digital adquirido</li>
        <li data-id="element-142">
          Comunicaciones relacionadas con tu compra (confirmación,
          actualizaciones del producto)
        </li>
      </ul>

      <h3 className="text-base font-bold text-navy" data-id="element-143">3. Protección de datos</h3>
      <p data-id="element-144">
        Utilizamos medidas de seguridad estándar de la industria para proteger
        tu información personal. Los pagos son procesados a través de
        plataformas seguras de terceros y no almacenamos datos de tarjetas de
        crédito.
      </p>

      <h3 className="text-base font-bold text-navy" data-id="element-145">
        4. Compartir información
      </h3>
      <p data-id="element-146">
        No vendemos, intercambiamos ni transferimos tu información personal a
        terceros. La única excepción son los proveedores de servicios que nos
        ayudan a operar nuestro negocio (procesadores de pago, plataforma de
        entrega de email).
      </p>

      <h3 className="text-base font-bold text-navy" data-id="element-147">5. Cookies</h3>
      <p data-id="element-148">
        Nuestro sitio puede utilizar cookies para mejorar tu experiencia de
        navegación y analizar el tráfico del sitio de forma anónima.
      </p>

      <h3 className="text-base font-bold text-navy" data-id="element-149">6. Tus derechos</h3>
      <p data-id="element-150">
        Puedes solicitar acceso, corrección o eliminación de tus datos
        personales en cualquier momento escribiéndonos por correo electrónico.
      </p>
    </>;
}
export function TermsContent() {
  return <>
      <p className="text-xs text-gray-400" data-id="element-151">
        Última actualización: febrero 2026
      </p>

      <h3 className="text-base font-bold text-navy pt-2" data-id="element-152">
        1. Producto digital
      </h3>
      <p data-id="element-153">
        "El Reset de 10 Minutos para tu Sistema Nervioso" es un producto digital
        (e-book) con fines educativos e informativos. Al completar tu compra,
        recibirás acceso inmediato al contenido en formato digital.
      </p>

      <h3 className="text-base font-bold text-navy" data-id="element-154">
        2. Descargo de responsabilidad médica
      </h3>
      <p data-id="element-155">
        Este producto <strong data-id="element-156">no sustituye</strong> diagnóstico, tratamiento
        médico, terapia psicológica ni consejo profesional de salud. El
        contenido es de carácter educativo y está basado en técnicas generales
        de regulación del sistema nervioso. Si experimentas síntomas severos de
        ansiedad, consulta a un profesional de salud.
      </p>

      <h3 className="text-base font-bold text-navy" data-id="element-157">
        3. Propiedad intelectual
      </h3>
      <p data-id="element-158">
        Todo el contenido del e-book es propiedad de La Guía Bilingüe. Queda
        prohibida su reproducción, distribución o reventa sin autorización
        expresa por escrito.
      </p>

      <h3 className="text-base font-bold text-navy" data-id="element-159">
        4. Política de reembolso
      </h3>
      <p data-id="element-160">
        Debido a la naturaleza digital del producto y su entrega inmediata,
        todas las ventas son finales. No se ofrecen reembolsos una vez
        completada la descarga.
      </p>

      <h3 className="text-base font-bold text-navy" data-id="element-161">5. Resultados</h3>
      <p data-id="element-162">
        Los resultados pueden variar de persona a persona. No garantizamos
        resultados específicos. El contenido ofrece herramientas prácticas, pero
        su efectividad depende de la práctica individual y consistente.
      </p>

      <h3 className="text-base font-bold text-navy" data-id="element-163">6. Modificaciones</h3>
      <p data-id="element-164">
        Nos reservamos el derecho de actualizar estos términos en cualquier
        momento. Los cambios serán efectivos al ser publicados en esta página.
      </p>
    </>;
}